import { observer } from "mobx-react-lite";
import { css } from "@/domains/emotion";
import { noop } from "lodash-es";
import { UNTITLED_NOTE_TITLE } from "@/domains/untitled/untitled";
import { UNTITLED_TEMPLATE_TITLE } from "@/domains/untitled/untitled";
import { ShareSheetPermissionRow } from "@/components/modal/share-sheet/share-sheet-permission-row/share-sheet-permission-row";
import { ShareSheetModalProps } from "@/components/modal/share-sheet/share-sheet-modal";
import { useLastNonNullRender } from "@/domains/react/useLastNonNullRender";
import { ShareSheetEntityKind } from "@/components/modal/share-sheet/types";
import {
  MDSButton,
  MDSButtonSlotName,
  MDSCheckbox,
  MDSIcon,
  MDSButtonVariant,
  MDSText,
  MDSTextVariant,
  MDSDialog,
  MDSDialogHeaderContent,
  MDSDialogSlotName,
  MDSChipInput,
  MDSDialogChip,
  MDSIconKind,
  MDSDialogChipVariant,
  MDSFadeVerticalScroll,
  MDSTextFieldVariant,
} from "@/mds";
import { MDSTooltip, MDSTooltipPlacement } from "@/mds/components/tooltip";
import styled from "@emotion/styled";

export const ShareSheetModalContent = observer<ShareSheetModalProps>(
  function ShareSheetModalContent({ store: { modal: store } }) {
    return useLastNonNullRender(() => {
      if (!store) return null;

      const titleChip = (() => {
        if (store.entityKind === ShareSheetEntityKind.Collection && store.collection) {
          return (
            <MDSDialogChip
              label={store.collection.label}
              variant={MDSDialogChipVariant.Collection}
              collectionId={store.collection.id}
            />
          );
        }
        if (store.entityKind === ShareSheetEntityKind.Note && store.note) {
          return (
            <MDSDialogChip
              label={store.note.title || UNTITLED_NOTE_TITLE}
              variant={MDSDialogChipVariant.Note}
            />
          );
        }
        if (store.entityKind === ShareSheetEntityKind.Template && store.template) {
          return (
            <MDSDialogChip
              label={store.template.title || UNTITLED_TEMPLATE_TITLE}
              variant={MDSDialogChipVariant.Template}
            />
          );
        }
      })();
      if (!titleChip) return null;

      const isShareDisabled = !store.chips.length || store.isSharing;
      return (
        <MDSDialog isOpen handleClose={() => store.close()}>
          <MDSDialogHeaderContent slot={MDSDialogSlotName.Header} allowOverflow={false}>
            <MDSText variant={MDSTextVariant.HeaderL}>Share</MDSText>
            {titleChip}
          </MDSDialogHeaderContent>

          <StyledModalContent
            slot={MDSDialogSlotName.Content}
            onKeyDownCapture={e => {
              if (e.key !== "Escape") return;

              e.preventDefault();
              e.stopPropagation();
              store.close();
            }}
          >
            <div className={rowStyles}>
              <MDSChipInput
                autoFocus
                textFieldVariant={MDSTextFieldVariant.Emphasis}
                isDisabled={store.isSharing}
                getChips={store.getChips}
                chips={store.chips}
                setChips={store.setChips}
                onSubmit={isShareDisabled ? noop : store.handleSubmit}
                placeholder={
                  store.entityKind === ShareSheetEntityKind.Collection
                    ? "Share with people or emails"
                    : "Share with people, emails, or collections"
                }
              />
              <StyledShareButton
                variant={
                  !store.isSharing ? MDSButtonVariant.Emphasis : MDSButtonVariant.Transparent
                }
                isDisabled={isShareDisabled}
                isLoading={store.isSharing}
                onClick={store.handleSubmit}
              >
                Share
              </StyledShareButton>
            </div>

            <MDSFadeVerticalScroll className={scrollableSectionStyles}>
              <div className={aclEntryStyles}>
                {store.aclEntries.map((permission, i) => (
                  <ShareSheetPermissionRow
                    key={i}
                    isUnsharing={store.isUnsharing}
                    permission={permission}
                    isGuestMode={store.isGuestMode}
                    canRevokeAccess={store.canRevokeAccess}
                    canUpdateAccess={store.canUpdateAccess}
                    handleRevokeAccess={store.handleRevokeAccess}
                    handleUpdateAccess={store.handleUpdateAccess}
                    handleCancelOperation={store.handleCancelOperation}
                    shareSheetModalStore={store}
                  />
                ))}
              </div>
            </MDSFadeVerticalScroll>
          </StyledModalContent>

          <StyledModalFooter slot={MDSDialogSlotName.Footer}>
            <MDSTooltip
              config={{
                label: "Only people added above can use this link",
                placement: MDSTooltipPlacement.Top,
              }}
            >
              <StyledCopyLinkButton
                variant={MDSButtonVariant.Transparent}
                onClick={store.handleCopyCollaborativeLink}
              >
                <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.Link} />
                Copy collaborative link
              </StyledCopyLinkButton>
            </MDSTooltip>
            {store.isEditableByAiCheckboxVisible && (
              <MDSCheckbox
                isSelected={store.isEditableByAi}
                onChange={store.handleToggleEditableByAi}
              >
                Allow Mem to edit
              </MDSCheckbox>
            )}
          </StyledModalFooter>
        </MDSDialog>
      );
    });
  }
);

const StyledModalContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-right: 8px;
  gap: ${({ theme }) => theme.spacing["2"]};
  padding-bottom: ${({ theme }) => theme.spacing["2"]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
`;

const StyledModalFooter = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing["2"]};
  padding-right: ${({ theme }) => theme.spacing[2]};
  padding-top: ${({ theme }) => theme.spacing[2]};
  @media (max-width: 768px) {
    padding: 16px 24px 24px 24px;
    flex-direction: column;
    gap: 4px;
  }
`;

const rowStyles = css({
  width: "100%",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "8px",
});

const aclEntryStyles = css({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "8px",
});

const scrollableSectionStyles = css({
  maxHeight: "max(120px, calc(100vh - 300px))",
});

const StyledShareButton = styled(MDSButton)`
  height: 40px;
`;

const StyledCopyLinkButton = styled(MDSButton)`
  color: ${({ theme }) => theme.colors.text.emphasis};
  & .mds-icon {
    color: ${({ theme }) => theme.colors.text.emphasis};
  }
`;
